import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  VetaCenterReadinessItemKind,
  VetaCenterReadinessRecordType,
  VetaCenterReadinessSeverity,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import useVetaCenter from '~hooks/useVetaCenter/useVetaCenter';
import useVetaCenterPollWindow from '~hooks/useVetaCenter/useVetaCenterPollWindow';
import {
  getGapLabel,
  groupReadinessRecords,
  ReadinessRecord,
} from '~hooks/useVetaCenter/vetaCenterReadinessRecords';
import useVetaCenterReadinessItemsQuery from '~queries/useVetaCenterReadinessItemsQuery';
import { ROUTES as routes, settingsPagePath } from '~router/routePaths';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import isEnumValue from '~utils/isEnumValue';

import { OWN_FLEET_HAULER_COMPANY_SECTION_ID } from './VetaCenterOwnFleetForm';
import {
  MaterialCodeEditor,
  SourceOperatorEditor,
} from './VetaCenterReadinessInlineEditors';

const KEY_PREFIX = 'administration.integration.veta_center.readiness';

interface GapLink {
  labelKey: string;
  to: string;
  type: 'section' | 'settings';
}

const vendorsLink: GapLink = {
  labelKey: `${KEY_PREFIX}.links.vendors`,
  to: settingsPagePath(routes.settings.vendors),
  type: 'settings',
};
const sitesLink: GapLink = {
  labelKey: `${KEY_PREFIX}.links.sites`,
  to: settingsPagePath(routes.settings.sitesManagement),
  type: 'settings',
};
const equipmentLink: GapLink = {
  labelKey: `${KEY_PREFIX}.links.equipment`,
  to: settingsPagePath(routes.settings.equipmentManagement),
  type: 'settings',
};

const gapLinks = {
  [VetaCenterReadinessItemKind.DOT_NUMBER_MISSING]: vendorsLink,
  [VetaCenterReadinessItemKind.DRIVER_CLASSIFICATION_MISSING]: {
    labelKey: `${KEY_PREFIX}.links.equipment_types`,
    to: settingsPagePath(routes.settings.equipmentTypeManagement),
    type: 'settings',
  },
  [VetaCenterReadinessItemKind.EXTERNAL_ID_MISSING]: sitesLink,
  [VetaCenterReadinessItemKind.LICENSE_NUMBER_MISSING]: equipmentLink,
  [VetaCenterReadinessItemKind.MATERIAL_CODE_MISSING]: null,
  [VetaCenterReadinessItemKind.MAX_GROSS_WEIGHT_MISSING]: equipmentLink,
  [VetaCenterReadinessItemKind.NOT_REGISTERED]: null,
  [VetaCenterReadinessItemKind.OVERWEIGHT_PERMIT_NUMBER_MISSING]: equipmentLink,
  [VetaCenterReadinessItemKind.OWN_FLEET_HAULER_COMPANY_MISSING]: {
    labelKey: `${KEY_PREFIX}.links.own_fleet`,
    to: OWN_FLEET_HAULER_COMPANY_SECTION_ID,
    type: 'section',
  },
  [VetaCenterReadinessItemKind.SITE_ADDRESS_MISSING]: sitesLink,
  [VetaCenterReadinessItemKind.SITE_STATE_UNSUPPORTED]: sitesLink,
  [VetaCenterReadinessItemKind.SOURCE_OPERATOR_DEFAULT_MISSING]: null,
  [VetaCenterReadinessItemKind.VENDOR_ACCOUNT_MISSING]: vendorsLink,
  [VetaCenterReadinessItemKind.VENDOR_DOT_NUMBER_MISSING]: vendorsLink,
} satisfies Record<VetaCenterReadinessItemKind, GapLink | null>;

const groupTitleKeys = {
  [VetaCenterReadinessRecordType.ACCOUNT]: `${KEY_PREFIX}.groups.account`,
  [VetaCenterReadinessRecordType.EQUIPMENT]: `${KEY_PREFIX}.groups.equipment`,
  [VetaCenterReadinessRecordType.EQUIPMENT_TYPE]: `${KEY_PREFIX}.groups.equipment_type`,
  [VetaCenterReadinessRecordType.SITE]: `${KEY_PREFIX}.groups.site`,
  [VetaCenterReadinessRecordType.MATERIAL]: `${KEY_PREFIX}.groups.material`,
} satisfies Record<VetaCenterReadinessRecordType, string>;

const hasBlock = (record: ReadinessRecord) =>
  record.items.some((item) => item.severity === VetaCenterReadinessSeverity.BLOCK);

const hasKind = (record: ReadinessRecord, kind: VetaCenterReadinessItemKind) =>
  record.items.some((item) => item.kind === kind);

const isReadyToRegister = (record: ReadinessRecord) =>
  hasKind(record, VetaCenterReadinessItemKind.NOT_REGISTERED) && !hasBlock(record);

const scrollToSection = (sectionId: string) =>
  document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });

interface VetaCenterReadinessProps {
  companyId: string;
  hasProjectLinks: boolean;
}

export default function VetaCenterReadiness({
  companyId,
  hasProjectLinks,
}: VetaCenterReadinessProps) {
  const { toasterStore } = useStores();
  const { register } = useVetaCenter();
  const [isRegistering, setIsRegistering] = useState(false);
  const { isPolling, startPolling, stopPolling } = useVetaCenterPollWindow();
  const { isError, isFetching, isLoading, items, refetch } =
    useVetaCenterReadinessItemsQuery(companyId, {
      isEnabled: hasProjectLinks,
      isPolling,
    });
  const isMutatingRef = useRef(false);

  const records = groupReadinessRecords(items);
  const readyCount = records.filter(isReadyToRegister).length;
  const toFixCount = records.filter(hasBlock).length;
  const hasNothingToRegister = readyCount === 0;

  useEffect(() => {
    if (hasNothingToRegister) stopPolling();
  }, [hasNothingToRegister, stopPolling]);

  const handleRegisterClick = async () => {
    if (isMutatingRef.current) return;
    isMutatingRef.current = true;

    setIsRegistering(true);
    try {
      if (!(await register(companyId))) return;

      toasterStore.push(alert(t(`${KEY_PREFIX}.toast_registering`), AlertTypes.success));
      startPolling();
      await refetch();
    } finally {
      isMutatingRef.current = false;
      setIsRegistering(false);
    }
  };

  const renderRecord = (record: ReadinessRecord) => {
    const links = [
      ...new Map(
        record.items
          .map((item) =>
            isEnumValue(VetaCenterReadinessItemKind, item.kind)
              ? gapLinks[item.kind]
              : null,
          )
          .filter((link) => link !== null)
          .map((link) => [link.to, link]),
      ).values(),
    ];

    return (
      <Box
        data-test-id={`veta-center-readiness-${record.id}`}
        key={`${record.type}/${record.id}`}
        sx={{ borderBottom: 1, borderColor: 'divider', display: 'grid', gap: 1, py: 1.5 }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontWeight: 600, mr: 1 }}>{record.name}</Typography>
          {record.items.map((item) => (
            <Chip
              color={
                item.severity === VetaCenterReadinessSeverity.BLOCK ? 'error' : 'warning'
              }
              data-test-id={`veta-center-gap-${item.severity}`}
              key={item.kind}
              label={getGapLabel(item.kind)}
              size="small"
              variant="outlined"
            />
          ))}
          {links.map((link) =>
            link.type === 'section' ? (
              <SmallButton
                color="brandV2Black"
                key={link.to}
                onClick={() => scrollToSection(link.to)}
                sx={{ color: 'text.primary' }}
                variant="text"
              >
                {t(link.labelKey)}
              </SmallButton>
            ) : (
              <Link key={link.to} to={link.to}>
                {t(link.labelKey)}
              </Link>
            ),
          )}
        </Box>
        {hasKind(record, VetaCenterReadinessItemKind.MATERIAL_CODE_MISSING) ? (
          <MaterialCodeEditor
            companyId={companyId}
            onSaved={refetch}
            recordId={record.id}
          />
        ) : null}
        {hasKind(record, VetaCenterReadinessItemKind.SOURCE_OPERATOR_DEFAULT_MISSING) ? (
          <SourceOperatorEditor
            companyId={companyId}
            onSaved={refetch}
            recordId={record.id}
          />
        ) : null}
      </Box>
    );
  };

  const renderBody = () => {
    if (isError) {
      return (
        <Alert severity="error">
          {t('error_messages.veta_center.failed_to_fetch_readiness_items')}
        </Alert>
      );
    }

    if (isLoading) return <CircularProgress size={24} />;

    if (!hasProjectLinks) {
      return (
        <Typography sx={{ color: 'text.secondary' }}>
          {t(`${KEY_PREFIX}.no_projects`)}
        </Typography>
      );
    }

    if (records.length === 0) {
      return <Alert severity="success">{t(`${KEY_PREFIX}.all_ready`)}</Alert>;
    }

    return Object.values(VetaCenterReadinessRecordType).map((type) => {
      const typeRecords = records.filter((record) => record.type === type);

      return typeRecords.length > 0 ? (
        <Box key={type} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
            {t(groupTitleKeys[type])}
          </Typography>
          {typeRecords.map(renderRecord)}
        </Box>
      ) : null;
    });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
        <Typography variant="h6">{t(`${KEY_PREFIX}.title`)}</Typography>
        <SmallLoadingButton
          color="brandV2Black"
          disabled={!hasProjectLinks}
          loading={isFetching}
          onClick={() => refetch()}
          sx={{ borderColor: 'divider', color: 'text.primary' }}
          variant="outlined"
        >
          {t(`${KEY_PREFIX}.refresh`)}
        </SmallLoadingButton>
      </Box>
      <Typography sx={{ color: 'text.secondary', my: 1 }}>
        {t(`${KEY_PREFIX}.description`)}
      </Typography>
      {hasProjectLinks && records.length > 0 ? (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 2, mb: 1 }}>
          <Typography>
            {t(`${KEY_PREFIX}.summary`, { ready: readyCount, toFix: toFixCount })}
          </Typography>
          <SmallLoadingButton
            disabled={hasNothingToRegister || isPolling}
            loading={isRegistering}
            onClick={handleRegisterClick}
            variant="contained"
          >
            {hasNothingToRegister
              ? t(`${KEY_PREFIX}.nothing_to_register`)
              : t(`${KEY_PREFIX}.register`, { count: readyCount })}
          </SmallLoadingButton>
        </Box>
      ) : null}
      {renderBody()}
    </Box>
  );
}
